import { Icon } from "@/components/ui/icon";
import { getFormattingLocale } from "@/i18n";
import {
    formatDisplayTimeMinutes,
    parseDisplayTimeMinutes,
    serializeTimeMinutes,
} from "@/lib/date";
import { cn } from "@/lib/utils";
import {
    useRef,
    useState,
    type ChangeEvent,
    type FocusEvent,
    type KeyboardEvent,
} from "react";
import { useTranslation } from "react-i18next";

type TimeFieldProps = {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    className?: string;
    testID?: string;
};

export function TimeField({
    value,
    onChange,
    placeholder,
    className,
    testID,
}: TimeFieldProps) {
    const { t } = useTranslation();
    const tPlaceholder = placeholder ?? t("ui.selectTime");
    const nativePickerRef = useRef<HTMLInputElement>(null);
    const formattedValue = value ? formatDisplayTimeMinutes(value) : "";
    const nativeValue = serializeTimeMinutes(value) ?? "";
    const [isFocused, setIsFocused] = useState(false);
    const [draft, setDraft] = useState("");
    const displayValue = isFocused ? draft : formattedValue;

    function commitDraft(raw: string) {
        const trimmed = raw.trim();

        if (trimmed === "") {
            onChange("");
            setDraft("");
            return;
        }

        const parsed = parseDisplayTimeMinutes(raw);

        if (parsed) {
            onChange(parsed);
            setDraft(formatDisplayTimeMinutes(parsed));
            return;
        }

        setDraft(formattedValue);
    }

    function openNativePicker() {
        const input = nativePickerRef.current;

        if (!input) {
            return;
        }

        try {
            if (typeof input.showPicker === "function") {
                input.showPicker();
                return;
            }
        } catch {
            // showPicker throws when the browser blocks it; fall back to click.
        }

        input.focus();
        input.click();
    }

    function handleFocus(event: FocusEvent<HTMLInputElement>) {
        setIsFocused(true);
        setDraft(formattedValue);
        event.currentTarget.select();
    }

    function handleChange(event: ChangeEvent<HTMLInputElement>) {
        const next = event.target.value;
        setDraft(next);

        if (next.trim() === "") {
            onChange("");
            return;
        }

        const parsed = parseDisplayTimeMinutes(next);

        if (parsed) {
            onChange(parsed);
        }
    }

    function handleBlur(event: FocusEvent<HTMLInputElement>) {
        setIsFocused(false);
        commitDraft(event.currentTarget.value);
    }

    function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
        if (event.key === "Enter") {
            event.preventDefault();
            event.currentTarget.blur();
        }

        if (event.key === "Escape") {
            event.preventDefault();
            setDraft(formattedValue);
            event.currentTarget.value = formattedValue;
            event.currentTarget.blur();
        }
    }

    return (
        <div
            className={cn(
                "border-input bg-background relative inline-flex h-8 w-[144px] shrink-0 items-center rounded-full border pl-3 pr-1.5 shadow-sm shadow-black/5",
                className,
            )}
        >
            <span className="relative min-w-0 flex-1">
                <input
                    type="text"
                    lang={getFormattingLocale()}
                    inputMode="text"
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                    value={displayValue}
                    placeholder={tPlaceholder}
                    data-testid={testID}
                    aria-label={displayValue || tPlaceholder}
                    onFocus={handleFocus}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    className="text-foreground placeholder:text-muted-foreground w-full min-w-0 truncate bg-transparent text-center font-sans text-sm leading-5 outline-none"
                />
            </span>
            <button
                type="button"
                tabIndex={-1}
                aria-label={tPlaceholder}
                onMouseDown={(event) => {
                    event.preventDefault();
                }}
                onClick={openNativePicker}
                className="text-muted-foreground flex size-6 shrink-0 items-center justify-center"
            >
                <Icon
                    name="clock"
                    size={12}
                    className="text-muted-foreground"
                />
            </button>
            <input
                ref={nativePickerRef}
                type="time"
                step={60}
                tabIndex={-1}
                aria-hidden
                value={nativeValue}
                onChange={(event) => {
                    const next = serializeTimeMinutes(event.target.value) ?? "";
                    onChange(next);
                    setDraft(next ? formatDisplayTimeMinutes(next) : "");
                }}
                className="pointer-events-none absolute inset-0 opacity-0"
            />
        </div>
    );
}
